import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Download,
  FileSpreadsheet,
  Database,
  CheckCircle2,
  Loader2,
  HardDrive,
  Calendar,
  FileJson,
  Monitor,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BackupPage = ({ user, settings }) => {
  const [loading, setLoading] = useState(false);
  const [loadingJson, setLoadingJson] = useState(false);
  const [lastBackup, setLastBackup] = useState(
    localStorage.getItem("lastBackupDate") || null
  );

  const handleExport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/backup/export`, {
        withCredentials: true,
        responseType: "blob",
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      
      // Get filename from response headers or generate one
      const contentDisposition = response.headers["content-disposition"];
      let filename = `Dossierya_Sauvegarde_${new Date().toISOString().slice(0, 10)}.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // Save last backup date
      const now = new Date().toLocaleString("fr-CA");
      setLastBackup(now);
      localStorage.setItem("lastBackupDate", now);

      toast.success("Sauvegarde téléchargée avec succès !");
    } catch (error) {
      console.error("Error exporting backup:", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setLoading(false);
    }
  };

  const handleExportJson = async () => {
    setLoadingJson(true);
    try {
      const response = await axios.get(`${API}/backup/export-json`, {
        withCredentials: true,
        responseType: "blob",
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      
      // Get filename from response headers or generate one
      const contentDisposition = response.headers["content-disposition"];
      let filename = `dossierya_backup_${new Date().toISOString().slice(0, 10)}.json`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Export JSON téléchargé ! Utilisez ce fichier pour l'application Windows.");
    } catch (error) {
      console.error("Error exporting JSON:", error);
      toast.error("Erreur lors de l'export JSON");
    } finally {
      setLoadingJson(false);
    }
  };

  return (
    <Layout user={user} settings={settings}>
      <div className="space-y-6" data-testid="backup-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Sauvegarde des données
          </h1>
          <p className="text-muted-foreground mt-1">
            Exportez toute votre base de données en fichier Excel
          </p>
        </div>

        {/* Main Card */}
        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5 text-primary" />
              Export Excel complet
            </CardTitle>
            <CardDescription>
              Téléchargez une sauvegarde complète de toutes vos données
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* What's included */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <h3 className="font-medium text-sm">Contenu du tableau général :</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  Code Client
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  Nom du client
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  Téléphone et Adresse courriel
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  État civil et Conjoint(e)
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  Signature formulaire + Date
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  Paiement + Date + Montant payé
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  État transmission TED
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  Notes au dossier
                </li>
              </ul>
            </div>

            {/* Last backup info */}
            {lastBackup && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                Dernière sauvegarde : {lastBackup}
              </div>
            )}

            {/* Export Button */}
            <Button
              onClick={handleExport}
              disabled={loading}
              size="lg"
              className="w-full"
              data-testid="export-button"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Génération en cours...
                </>
              ) : (
                <>
                  <Download className="h-5 w-5 mr-2" />
                  Télécharger la sauvegarde Excel
                </>
              )}
            </Button>

            {/* File info */}
            <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <FileSpreadsheet className="h-4 w-4" />
                Format : Excel (.xlsx)
              </span>
              <span className="flex items-center gap-1">
                <HardDrive className="h-4 w-4" />
                Tableau Général + Résumé
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Tips */}
        <Card className="max-w-2xl border-blue-200 bg-blue-50/50">
          <CardContent className="pt-6">
            <h3 className="font-medium text-blue-800 mb-2">💡 Conseils</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Effectuez une sauvegarde régulièrement (idéalement quotidienne)</li>
              <li>• Conservez vos fichiers de sauvegarde dans un endroit sûr</li>
              <li>• Gardez plusieurs versions de sauvegarde (ex: 7 derniers jours)</li>
            </ul>
          </CardContent>
        </Card>

        {/* Export for Windows App */}
        <Card className="max-w-2xl border-purple-200 bg-purple-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-purple-800">
              <Monitor className="h-5 w-5" />
              Export pour Application Windows
            </CardTitle>
            <CardDescription className="text-purple-700">
              Exportez vos données pour les utiliser dans l'application Windows locale
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-white/50 rounded-lg p-4 space-y-2">
              <p className="text-sm text-purple-700">
                Ce fichier JSON contient toutes vos données (clients, paiements, formulaires, transmissions TED) 
                et peut être importé dans l'application Dossierya pour Windows.
              </p>
            </div>
            
            <Button
              onClick={handleExportJson}
              disabled={loadingJson}
              variant="outline"
              className="w-full border-purple-300 text-purple-700 hover:bg-purple-100"
              data-testid="export-json-button"
            >
              {loadingJson ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Génération en cours...
                </>
              ) : (
                <>
                  <FileJson className="h-5 w-5 mr-2" />
                  Télécharger l'export JSON (pour Windows)
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default BackupPage;
